/**
 * Persistencia local del feature "Escuchar": preferencias de reproducción en
 * localStorage (sobreviven entre visitas) + snapshot de la sesión de escucha
 * en sessionStorage (sobrevive a F5, muere con la pestaña). Funciones puras
 * best-effort: storage no disponible / corrupto → defaults.
 */

import type { Cefr, ChapterRef } from './types.js';

// ─── Preferencias de reproducción (localStorage) ─────────────────────────────
const LS_KEY = 'orborix-listen-v1';
export const SPEED_OPTIONS = [0.5, 0.75, 0.9, 1, 1.1, 1.25, 1.5] as const;
export const DEFAULT_SPEED = 1;

export interface PersistedPrefs {
  speed: number;
  karaoke: boolean;
  /**
   * Nivel que el alumno ELIGIÓ a mano en el selector (o al elegir una voz de
   * otro nivel). `null` = nunca eligió: la Audioteca sigue el nivel de su
   * cuenta. Antes se guardaba `level` con cualquier nivel cargado, también el
   * que ponía la app sola, y después se leía como elección: el nivel quedaba
   * pegado al navegador aunque la cuenta cambiara de nivel (o de cuenta).
   * Por eso el campo viejo `level` se ignora a propósito.
   */
  chosenLevel: Cefr | null;
  voiceId: string | null;
  /** Idioma de narración preferido (en/es). Se prefiere ESTO sobre `voiceId` al
   * abrir un capítulo: las voces son por-libro (branding gender-matched), así que
   * forzar la voz exacta de otro libro garantizaba un 404. El idioma sí viaja. */
  lang: string | null;
  /** Auto-avanzar al próximo capítulo al terminar (panel de fin, 19.G). */
  autoAdvance: boolean;
}

export function loadPrefs(): PersistedPrefs {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<PersistedPrefs>;
      return {
        speed:
          typeof p.speed === 'number' && SPEED_OPTIONS.includes(p.speed as never)
            ? p.speed
            : DEFAULT_SPEED,
        karaoke: p.karaoke ?? true,
        chosenLevel: p.chosenLevel ?? null,
        voiceId: p.voiceId ?? null,
        lang: p.lang ?? null,
        autoAdvance: p.autoAdvance ?? true,
      };
    }
  } catch {
    /* localStorage no disponible / corrupto → defaults */
  }
  return {
    speed: DEFAULT_SPEED,
    karaoke: true,
    chosenLevel: null,
    voiceId: null,
    lang: null,
    autoAdvance: true,
  };
}

export function savePrefs(p: PersistedPrefs): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(p));
  } catch {
    /* best-effort */
  }
}

// ─── Sesión de escucha (sobrevive a F5) ──────────────────────────────────────
// Snapshot del capítulo abierto en sessionStorage (por pestaña): un reload no
// debe perder la sesión — el player la rehidrata vía `restoreSession()`.
// `endSession` (X del player) la limpia; cerrar la pestaña también (semántica
// de sessionStorage, igual que `coachSessions`: la "visita" es la pestaña).
const SS_KEY = 'orborix-listen-session-v1';

export interface SessionSnapshot {
  ref: ChapterRef;
  pos: number;
  cefr: Cefr | null;
}

export function loadSession(): SessionSnapshot | null {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<SessionSnapshot>;
    if (
      !s.ref ||
      typeof s.ref.book !== 'string' ||
      typeof s.ref.edition !== 'string' ||
      typeof s.ref.keypoint !== 'string'
    ) {
      return null;
    }
    return {
      ref: s.ref,
      pos: typeof s.pos === 'number' && Number.isFinite(s.pos) ? s.pos : 0,
      cefr: s.cefr ?? null,
    };
  } catch {
    return null;
  }
}

export function saveSession(s: SessionSnapshot): void {
  try {
    sessionStorage.setItem(SS_KEY, JSON.stringify(s));
  } catch {
    /* best-effort */
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(SS_KEY);
  } catch {
    /* best-effort */
  }
}
